import React, { useState, useEffect } from 'react';
import { Icon, ParticleCanvas, AdBanner } from '../components/primitives';
import { RecentlyViewed } from '../components/cards';
import { apiFetch, formatPrice, placeholderImg, handleImgError } from '../lib/utils';

let _heroHasAnimated = false;

export default function LandingView({ onNavigate, onViewEquipment, adConfig = {} }) {
  const [searchVal, setSearchVal] = useState('');
  const [featuredItems, setFeaturedItems] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [typeCounts, setTypeCounts] = useState({});
  const [stats, setStats] = useState({ dealers: 0, equipment: 0, states: 0, brands: 0 });
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [flippedCard, setFlippedCard] = useState(null);

  const SkeletonValue = ({ value, width = 48 }) =>
    value
      ? <>{value}</>
      : <span className="skeleton" style={{ display: 'inline-block', width, height: '0.9em', borderRadius: 3, verticalAlign: 'middle' }} />;

  useEffect(() => {
    if (!_heroHasAnimated) {
      setShouldAnimate(true);
      _heroHasAnimated = true;
    }
  }, []);

  useEffect(() => {
    apiFetch('/equipment?hasImage=true&limit=20')
      .then(d => {
        const allItems = (d.data || []).filter(i => i.image_url);
        setFeaturedItems(allItems.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    apiFetch('/stats')
      .then(d => setStats(d.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      apiFetch('/equipment?type=forklift&hasImage=true&limit=1'),
      apiFetch('/equipment?type=pallet_jack&hasImage=true&limit=1'),
      apiFetch('/equipment?type=reach_truck&hasImage=true&limit=1'),
      apiFetch('/equipment?type=telehandler&hasImage=true&limit=1'),
    ]).then(([f, p, r, t]) => {
      setTypeCounts({
        forklift: f.total || 0,
        pallet_jack: p.total || 0,
        reach_truck: r.total || 0,
        telehandler: t.total || 0,
      });
      // Pull the first image_url per type so every category card has a cover image,
      // regardless of which types happen to be in the "newest 20" featured pool.
      const imgs = {};
      for (const [key, resp] of [['forklift', f], ['pallet_jack', p], ['reach_truck', r], ['telehandler', t]]) {
        const first = resp?.data?.[0];
        if (first?.image_url) imgs[key] = first.image_url;
      }
      setCategoryImages(imgs);
    }).catch(() => {});
  }, []);

  function handleSearch(e) {
    e.preventDefault();
    onNavigate('inventory', searchVal.trim());
  }

  const ALL_CATEGORIES = [
    { key: 'forklift', label: 'Forklifts', href: '/forklifts', desc: 'Counterbalance & electric solutions for heavy logistics.' },
    { key: 'pallet_jack', label: 'Pallet Jacks', href: '/pallet-jacks', desc: 'Manual & powered warehouse movers.' },
    { key: 'reach_truck', label: 'Reach Trucks', href: '/reach-trucks', desc: 'High-bay narrow-aisle specialists.' },
    { key: 'telehandler', label: 'Telehandlers', href: '/telehandlers', desc: 'Telescoping boom material handlers.' },
  ];
  const activeCategories = ALL_CATEGORIES.filter(c => (typeCounts[c.key] || 0) > 0);
  const mainCategory = activeCategories[0] || ALL_CATEGORIES[0];
  const sideCategories = activeCategories.slice(1);

  return (
    <div>
      {/* Hero */}
      <div className={`landing-hero${shouldAnimate ? ' hero-animated' : ''}`}>
        {/* Particle canvas for hero */}
        <ParticleCanvas />

        {/* Animated 3D forklift anchor (right side) */}
        <div className="landing-hero-forklift" aria-hidden="true">
          <model-viewer
            src="/forklift_animated.glb"
            autoplay
            disable-zoom
            interaction-prompt="none"
            camera-controls
            camera-orbit="-20deg 80deg 4m"
            field-of-view="28deg"
            exposure="1.1"
            shadow-intensity="0.8"
            shadow-softness="1"
            environment-image="neutral"
          ></model-viewer>
        </div>

        {/* Hero content */}
        <div className="landing-hero-inner">
          <div>
            <span className="hero-anim-eyebrow landing-hero-eyebrow-block">Industrial Grade Marketplace</span>

            <h1
              className="hero-anim-headline-1 landing-hero-headline"
              style={{
                fontFamily: 'var(--font-head)',
                fontSize: 'clamp(40px, 6vw, 72px)',
                fontWeight: 700,
                letterSpacing: '-0.03em',
                textTransform: 'uppercase',
                lineHeight: 0.92,
                color: 'var(--on-surface)',
                marginBottom: 48,
              }}
            >
              <span className="hero-rotator" aria-label="Forklifts, pallet jacks, reach trucks, telehandlers">
                <span className="hero-rotator-track">
                  <span className="hero-rotator-item">Forklifts.</span>
                  <span className="hero-rotator-item">Pallet Jacks.</span>
                  <span className="hero-rotator-item">Reach Trucks.</span>
                  <span className="hero-rotator-item">Telehandlers.</span>
                  <span className="hero-rotator-item" aria-hidden="true">Forklifts.</span>
                </span>
              </span>
              <span className="hero-line">Every <span style={{ color: 'var(--primary-container)', mixBlendMode: 'lighten' }}>Dealer</span>,</span>
              <span className="hero-line">One Search.</span>
            </h1>

            {/* Integrated search bar */}
            <form
              className="hero-anim-search"
              onSubmit={handleSearch}
              style={{
                display: 'flex',
                flexDirection: 'row',
                background: 'var(--surface-container)',
                boxShadow: '0 24px 48px -12px rgba(0,0,0,0.5)',
                padding: 4,
                maxWidth: 672,
                border: '1px solid rgba(85,67,54,0.2)',
              }}
            >
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 16px', background: 'var(--surface-container)' }}>
                <Icon name="search" style={{ color: 'var(--outline)', fontSize: 20 }} />
                <input
                  className="landing-search-input"
                  placeholder="Search forklifts, reach trucks, pallet jacks..."
                  value={searchVal}
                  onChange={e => setSearchVal(e.target.value)}
                  aria-label="Search equipment"
                  style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--on-surface)', width: '100%', padding: '20px 12px', fontFamily: 'var(--font-body)', fontSize: 14 }}
                />
              </div>
              <button
                type="submit"
                style={{
                  background: 'var(--primary-container)',
                  color: 'var(--on-primary-container)',
                  padding: '20px 48px',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'background var(--transition-fast)',
                }}
              >
                Explore <Icon name="arrow_forward" style={{ fontSize: 14 }} />
              </button>
            </form>

            {/* Mobile tech specs strip */}
            <div className="landing-hero-tech-specs-mobile" aria-hidden="false">
              <div className="spec-item"><span className="spec-label">Equipment</span><span className="spec-value"><SkeletonValue value={stats.equipment ? stats.equipment.toLocaleString() : null} /></span></div>
              <div className="spec-item"><span className="spec-label">Dealers</span><span className="spec-value"><SkeletonValue value={stats.dealers ? stats.dealers.toLocaleString() : null} /></span></div>
              <div className="spec-item"><span className="spec-label">States</span><span className="spec-value"><SkeletonValue value={stats.states || null} width={36} /></span></div>
              <div className="spec-item"><span className="spec-label">Brands</span><span className="spec-value"><SkeletonValue value={stats.brands || null} width={36} /></span></div>
            </div>
          </div>
        </div>

        {/* Side tech specs ornament */}
        <div className="landing-hero-tech-specs hero-anim-stats">
          <div className="landing-hero-tech-spec-item">
            <div className="landing-hero-tech-spec-label">Equipment Listed</div>
            <div className="landing-hero-tech-spec-value"><SkeletonValue value={stats.equipment ? stats.equipment.toLocaleString() : null} /></div>
          </div>
          <div className="landing-hero-tech-spec-item">
            <div className="landing-hero-tech-spec-label">Dealers</div>
            <div className="landing-hero-tech-spec-value"><SkeletonValue value={stats.dealers ? stats.dealers.toLocaleString() : null} /></div>
          </div>
          <div className="landing-hero-tech-spec-item">
            <div className="landing-hero-tech-spec-label">States Covered</div>
            <div className="landing-hero-tech-spec-value"><SkeletonValue value={stats.states || null} width={36} /></div>
          </div>
          <div className="landing-hero-tech-spec-item">
            <div className="landing-hero-tech-spec-label">Brands</div>
            <div className="landing-hero-tech-spec-value"><SkeletonValue value={stats.brands || null} width={36} /></div>
          </div>
        </div>
        <div className="landing-hero-scroll-hint" aria-hidden="true">Scroll</div>
      </div>

      <div className="main" style={{ maxWidth: 'none', padding: 0 }}>
        {/* Strip 1: OEM Brand Strip */}
        <section className="brand-strip-section reveal">
          <div className="brand-strip-eyebrow">Inventory from leading manufacturers</div>
          <div className="brand-strip-row">
            {['Toyota', 'Crown', 'Hyster', 'Yale', 'Raymond', 'JLG', 'CAT'].map((b, i, a) => (
              <React.Fragment key={b}>
                <span className="brand-strip-name">{b}</span>
                {i < a.length - 1 && <span className="brand-strip-dot" aria-hidden="true">·</span>}
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* Categories Bento Grid */}
        <section style={{ padding: '96px 32px', maxWidth: 1440, margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '50%', left: '20%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(252,148,48,0.04) 0%, transparent 70%)', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 0 }} />
          <div className="reveal" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 48, position: 'relative', zIndex: 1 }}>
            <div>
              <h2 className="landing-h2">Browse by Category</h2>
              <p style={{ color: 'var(--outline)', marginTop: 8, maxWidth: 400, fontFamily: 'var(--font-body)' }}>Every category of warehouse equipment, from counterbalance forklifts to powered pallet jacks.</p>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('inventory')}>View All Categories</button>
          </div>
          <div className="landing-bento-grid reveal" style={sideCategories.length === 0 ? { gridTemplateColumns: '1fr' } : undefined}>
            {/* Main large card */}
            <a
              href={mainCategory.href}
              style={{
                background: 'var(--surface-container)',
                position: 'relative',
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
              }}
              className="category-bento-main landing-bento-main"
            >
              {categoryImages[mainCategory.key] && (
                <img
                  src={categoryImages[mainCategory.key]}
                  alt={mainCategory.label}
                  loading="lazy"
                  decoding="async"
                  className="bento-cat-img"
                  onError={(e) => handleImgError(e, mainCategory.key)}
                />
              )}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(19,19,19,0.95) 0%, rgba(19,19,19,0.4) 50%, rgba(19,19,19,0.2) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 32, left: 32 }}>
                <h3 style={{ fontFamily: 'var(--font-head)', fontSize: 30, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{mainCategory.label}</h3>
                <p style={{ color: 'var(--on-surface-var)', fontFamily: 'var(--font-body)', fontSize: 13, marginBottom: 4 }}>{mainCategory.desc}</p>
                <span style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700 }}>{typeCounts[mainCategory.key] || 0} models</span>
                <div style={{ marginTop: 16 }}>
                  <span
                    className="btn btn-sm"
                    style={{ background: 'rgba(58,57,57,0.8)', color: 'var(--on-surface)', border: 'none', backdropFilter: 'blur(4px)', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700 }}
                  >
                    Browse Models
                  </span>
                </div>
              </div>
            </a>
            {/* Side stack — only categories with data */}
            {sideCategories.length > 0 && (
              <div className="landing-bento-side" style={sideCategories.length === 1 ? { gridTemplateRows: '1fr' } : sideCategories.length === 2 ? { gridTemplateRows: '1fr 1fr' } : undefined}>
                {sideCategories.map(cat => (
                  <a
                    key={cat.href}
                    href={cat.href}
                    style={{
                      background: 'var(--surface-container)',
                      position: 'relative',
                      overflow: 'hidden',
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    {categoryImages[cat.key] && (
                      <img
                        src={categoryImages[cat.key]}
                        alt={cat.label}
                        loading="lazy"
                        decoding="async"
                        className="bento-cat-img"
                        onError={(e) => handleImgError(e, cat.key)}
                      />
                    )}
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(19,19,19,0.85) 0%, rgba(19,19,19,0.4) 100%)' }} />
                    <div style={{ position: 'absolute', bottom: 24, left: 24 }}>
                      <h3 style={{ fontFamily: 'var(--font-head)', fontSize: 18, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{cat.label}</h3>
                      <span style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 700 }}>{typeCounts[cat.key] || 0} models</span>
                      <div style={{ marginTop: 8 }}>
                        <span style={{ color: 'var(--primary)', fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Explore Models</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Featured High-Spec Inventory */}
        {featuredItems.length > 0 && (
          <section style={{ padding: '96px 0', background: 'var(--surface-lowest)', borderTop: '1px solid var(--outline-var)' }} className="brushed-metal">
            <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 32px' }}>
              <div className="reveal" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 64, flexWrap: 'wrap', gap: 16 }}>
                <h2 className="landing-h2">Featured High-Spec Inventory</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 128, height: 1, background: 'var(--outline-var)', opacity: 0.3 }} />
                  <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4em', fontWeight: 700, color: 'var(--outline)' }}>Top Picks</span>
                </div>
              </div>
              <div className="featured-grid-3 reveal-stagger">
                {featuredItems.map((item, idx) => (
                  <div key={item.id} className="flip-container">
                    <div className={`flip-card${flippedCard === idx ? ' flipped' : ''}`}>
                      {/* FRONT FACE */}
                      <div className="flip-card-front">
                        <div
                          className="featured-card-v2 reveal"
                          style={{
                            background: 'var(--surface-container)',
                            cursor: 'pointer',
                            transition: 'box-shadow 0.3s',
                          }}
                        >
                          <div style={{ aspectRatio: '4/3', position: 'relative', overflow: 'hidden' }}>
                            <img
                              src={item.image_url || placeholderImg(item.type, item.id)}
                              alt={item.name}
                              className={item.image_url ? 'has-real-image' : ''}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.7s' }}
                              loading="lazy"
                              decoding="async"
                              onError={(e) => handleImgError(e, item.type)}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                background: 'var(--primary-container)',
                                color: 'var(--on-primary-container)',
                                padding: '4px 8px',
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.2em',
                              }}
                            >
                              {item.badge || (item.condition === 'Factory New' ? 'Premium Condition' : item.condition === 'CPO' ? 'Certified Pre-Owned' : 'Heavy Duty')}
                            </div>
                          </div>
                          <div style={{ padding: 28 }}>
                            <div className="featured-card-title-row">
                              <h3 className="featured-card-title">{item.name}</h3>
                              <span className="featured-card-price">{formatPrice(item.price)}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                              {[
                                { label: 'Capacity', value: item.capacity || '—' },
                                { label: 'Fuel Type', value: item.fuel || '—' },
                                { label: 'Year', value: item.year || '—' },
                                { label: 'Condition', value: item.condition || '—' },
                              ].map(spec => (
                                <div key={spec.label}>
                                  <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'var(--outline)', marginBottom: 2 }}>{spec.label}</p>
                                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600 }}>{spec.value}</p>
                                </div>
                              ))}
                            </div>
                            <button
                              className="btn btn-ghost"
                              style={{ width: '100%', justifyContent: 'center', padding: '12px 16px', fontSize: 10, letterSpacing: '0.2em', display: 'flex', alignItems: 'center', gap: 8 }}
                              onClick={e => { e.stopPropagation(); setFlippedCard(idx); }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 14, fontVariationSettings: "'wght' 200" }} aria-hidden="true">flip</span>
                              Full Technical Specs
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* BACK FACE */}
                      <div className="flip-card-back">
                        <div className="back-title">{item.name}</div>
                        <div className="back-specs">
                          <div className="back-spec-item">
                            <span className="back-spec-label">Capacity</span>
                            <span className="back-spec-value">{item.capacity || '—'}</span>
                          </div>
                          <div className="back-spec-item">
                            <span className="back-spec-label">Fuel Type</span>
                            <span className="back-spec-value">{item.fuel || '—'}</span>
                          </div>
                          <div className="back-spec-item">
                            <span className="back-spec-label">Year</span>
                            <span className="back-spec-value">{item.year || '—'}</span>
                          </div>
                          <div className="back-spec-item">
                            <span className="back-spec-label">Condition</span>
                            <span className="back-spec-value">{item.condition || '—'}</span>
                          </div>
                          <div className="back-spec-item">
                            <span className="back-spec-label">Price</span>
                            <span className="back-spec-value" style={{ color: 'var(--primary)' }}>{formatPrice(item.price)}</span>
                          </div>
                          <div className="back-spec-item">
                            <span className="back-spec-label">Max Lift</span>
                            <span className="back-spec-value">{item.maxLift || item.max_lift || '—'}</span>
                          </div>
                        </div>
                        <div className="back-actions">
                          <button
                            className="btn btn-primary"
                            style={{ flex: 1, justifyContent: 'center' }}
                            onClick={e => { e.stopPropagation(); onViewEquipment ? onViewEquipment(item.id) : onNavigate('inventory'); }}
                          >
                            View Full Details
                          </button>
                          <button
                            className="btn btn-ghost"
                            onClick={e => { e.stopPropagation(); setFlippedCard(null); }}
                          >
                            Flip Back
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <AdBanner slot={adConfig.adsenseSlotHome} pubId={adConfig.adsensePubId} format="horizontal" />
      </div>

      {/* Strip 2: How It Works */}
      <section className="how-it-works-section reveal">
        <div className="how-it-works-inner">
          <div className="how-it-works-header">
            <div className="how-it-works-eyebrow">How it works</div>
            <h2 className="how-it-works-title">Marketplace in three steps</h2>
          </div>
          <div className="how-it-works-grid">
            <div>
              <div className="how-it-works-col-head">
                <span className="material-symbols-outlined how-it-works-col-icon" aria-hidden="true">shopping_cart</span>
                <span className="how-it-works-col-label">For buyers</span>
              </div>
              {[
                { n: '01', name: 'Browse', desc: 'Find equipment by category, brand, or state.' },
                { n: '02', name: 'Inquire', desc: 'Send a free direct inquiry to the dealer.' },
                { n: '03', name: 'Connect', desc: 'Get a direct reply from the dealer — no middleman.' },
              ].map(s => (
                <div className="how-it-works-step" key={s.n}>
                  <div className="how-it-works-step-num">{s.n}</div>
                  <div>
                    <div className="how-it-works-step-name">{s.name}</div>
                    <div className="how-it-works-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <div className="how-it-works-col-head">
                <span className="material-symbols-outlined how-it-works-col-icon" aria-hidden="true">storefront</span>
                <span className="how-it-works-col-label">For dealers</span>
              </div>
              {[
                { n: '01', name: 'Enroll', desc: 'Create a free dealer profile in under two minutes.' },
                { n: '02', name: 'List', desc: 'Add your equipment with photos and full specs.' },
                { n: '03', name: 'Convert', desc: 'Receive buyer inquiries nationwide.' },
              ].map(s => (
                <div className="how-it-works-step" key={s.n}>
                  <div className="how-it-works-step-num">{s.n}</div>
                  <div>
                    <div className="how-it-works-step-name">{s.name}</div>
                    <div className="how-it-works-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strip 3: Industry Expertise */}
      <section className="expertise-section reveal">
        <div className="expertise-inner">
          <div className="expertise-header">
            <div className="expertise-eyebrow">Industry expertise</div>
            <h2 className="expertise-title">From the LiftMarket knowledge base</h2>
          </div>
          <div className="expertise-grid reveal-stagger">
            {[
              {
                eyebrow: 'Buying',
                title: 'Forklift Buying Guide',
                desc: 'What to know before purchasing your first forklift.',
                href: '/guides/forklift-buying-guide',
              },
              {
                eyebrow: 'Inspection',
                title: 'Used Forklift Checklist',
                desc: 'A pre-purchase inspection checklist to avoid expensive surprises.',
                href: '/guides/used-forklift-inspection-checklist',
              },
              {
                eyebrow: 'Compliance',
                title: 'OSHA Safety & Certification',
                desc: 'The regulations every operator and dealer should know.',
                href: '/guides/forklift-safety-osha-certification',
              },
            ].map(g => (
              <a key={g.href} href={g.href} className="expertise-card reveal">
                <div className="expertise-card-eyebrow">{g.eyebrow}</div>
                <div className="expertise-card-title">{g.title}</div>
                <div className="expertise-card-desc">{g.desc}</div>
                <div className="expertise-card-link">Read guide
                  <span className="material-symbols-outlined" style={{ fontSize: 14, fontVariationSettings: "'wght' 300" }} aria-hidden="true">arrow_forward</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Section */}
      <section style={{ padding: '128px 0', background: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, right: '10%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(252,148,48,0.03) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 10 }}>
          <div className="partner-grid reveal">
            {/* Left: text content */}
            <div>
              <span style={{ color: 'var(--primary)', fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4em', marginBottom: 16, display: 'block' }}>Dealer Ecosystem</span>
              <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 'clamp(36px, 5vw, 60px)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '-0.03em', marginBottom: 32, lineHeight: 1 }}>
                Partner with <br /><span style={{ color: 'var(--outline)' }}>LiftMarket</span>
              </h2>
              <p style={{ color: 'var(--on-surface-var)', fontSize: 16, lineHeight: 1.7, marginBottom: 48, maxWidth: 480 }}>
                List your equipment for free and connect with buyers searching for forklifts, pallet jacks, and material handling equipment across all 50 states.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, marginBottom: 48 }}>
                {[
                  [(stats.dealers || 0).toLocaleString(), 'Enrolled Dealers'],
                  [(stats.equipment || 0).toLocaleString(), 'Equipment Listed'],
                ].map(([num, label]) => (
                  <div key={label} style={{ borderLeft: '2px solid var(--primary-container)', paddingLeft: 24 }}>
                    <p style={{ fontFamily: 'var(--font-head)', fontSize: 36, fontWeight: 700 }}>{num}</p>
                    <p style={{ color: 'var(--outline)', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.2em', fontWeight: 700 }}>{label}</p>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-primary"
                style={{ padding: '20px 48px', fontSize: 11, letterSpacing: '0.2em' }}
                onClick={() => onNavigate('enroll')}
              >
                Start Selling Today
              </button>
            </div>
            {/* Right: feature highlights */}
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 24 }}>
              {[
                { icon: 'storefront', title: 'Free to List', body: 'Create your dealer profile and list equipment at no cost. Upgrade to Pro or Elite for featured placement and priority visibility.' },
                { icon: 'search', title: 'Qualified Buyers', body: 'Connect with buyers actively searching for material handling equipment across all 50 states.' },
                { icon: 'bar_chart', title: 'Detailed Analytics', body: 'Pro and Elite dealers get inquiry analytics, lead tracking, and performance dashboards.' },
              ].map(f => (
                <div key={f.title} style={{ display: 'flex', gap: 20, alignItems: 'flex-start', background: 'var(--surface-container)', padding: 28, borderLeft: '3px solid var(--primary-container)' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 28, color: 'var(--primary-container)', flexShrink: 0, marginTop: 2 }}>{f.icon}</span>
                  <div>
                    <p style={{ fontFamily: 'var(--font-head)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 14, marginBottom: 6 }}>{f.title}</p>
                    <p style={{ color: 'var(--on-surface-var)', fontSize: 13, lineHeight: 1.6 }}>{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Abstract background shape */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '33%', height: '100%', background: 'var(--surface-lowest)', transform: 'skewX(12deg) translateX(50%)', zIndex: 0 }} />
      </section>

      <RecentlyViewed onViewEquipment={onViewEquipment} />

      {/* Strip 4: Trust Band */}
      <section className="trust-band reveal">
        <div className="trust-band-grid">
          <div className="trust-band-item">
            <div className="trust-band-value">
              {stats.brands ? stats.brands : <span className="skeleton trust-band-skeleton" />}
            </div>
            <div className="trust-band-label">Major brands<br/>supported</div>
          </div>
          <div className="trust-band-item">
            <div className="trust-band-value">
              {stats.states ? stats.states : <span className="skeleton trust-band-skeleton" />}
            </div>
            <div className="trust-band-label">States<br/>covered</div>
          </div>
          <div className="trust-band-item">
            <div className="trust-band-value">
              {stats.equipment ? stats.equipment.toLocaleString() : <span className="skeleton trust-band-skeleton" />}
            </div>
            <div className="trust-band-label">Equipment<br/>listings</div>
          </div>
          <div className="trust-band-item">
            <div className="trust-band-value">100%</div>
            <div className="trust-band-label">Free<br/>to list</div>
          </div>
        </div>
      </section>
    </div>
  );
}
